import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  Cpu,
  CheckCircle2,
  ChevronRight,
  Code2,
  Database,
  Globe2,
  Link2Off,
  Menu,
  PhoneMissed,
  Settings2,
  SunMoon,
  Timer,
  X
} from "lucide-react";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SeoHead } from "./seo";

const ACCENT = "var(--accent)";
const BRAND_LOGO = encodeURI("/mansok logo.jpg");

const LONG_CONTENT = {
  services: [
    {
      icon: <Globe2 className="h-6 w-6" />,
      title: "Growth Infrastructure",
      intro:
        "High-performance websites and conversion architecture that turn traffic into booked calls and qualified pipeline.",
      items: [
        "Performance-first web architecture with fast Core Web Vitals",
        "Landing pages engineered for conversion and clear offer positioning",
        "Lead capture funnels with booking, qualification, and tracking",
        "Technical SEO structure for stronger indexing and intent matching",
        "Analytics instrumentation and reporting for continuous optimization"
      ]
    },
    {
      icon: <Bot className="h-6 w-6" />,
      title: "Automation and AI Systems",
      intro:
        "Response-speed systems that recover missed opportunities and execute follow-up at machine speed.",
      items: [
        "Missed-call recovery with instant callback and qualification logic",
        "AI chat workflows for website and social lead response",
        "CRM automation for pipeline movement, reminders, and handoffs",
        "Operational workflows that remove repetitive admin bottlenecks",
        "SLA-based escalation and alerting for high-value opportunities"
      ]
    },
    {
      icon: <Code2 className="h-6 w-6" />,
      title: "Custom Business Software",
      intro:
        "Internal tools and software systems designed around your actual operations, not generic templates.",
      items: [
        "Custom dashboards for leadership visibility and accountability",
        "Operations tools for finance, sales, and service teams",
        "Role-based access and secure data architecture",
        "Integration layers for external APIs and third-party software",
        "Scalable architecture prepared for growth and product expansion"
      ]
    }
  ],
  systemPrinciples: [
    {
      title: "Simplicity over complexity",
      description: "Clean systems that anyone can understand and maintain."
    },
    {
      title: "Speed over perfection",
      description: "Ship fast, iterate based on real data, not assumptions."
    },
    {
      title: "Automation over manual work",
      description: "Remove repetitive tasks. Free your team to focus on growth."
    },
    {
      title: "Data over opinions",
      description: "Decisions backed by metrics, not guesswork."
    }
  ],
  process: [
    {
      step: "01",
      title: "Diagnose the bottleneck",
      description:
        "We audit your current sales, marketing, and operations flow to identify exactly where revenue and speed are being lost."
    },
    {
      step: "02",
      title: "Design the system",
      description:
        "We map the right architecture for your team, tools, and business model so execution is practical and measurable."
    },
    {
      step: "03",
      title: "Build fast and ship",
      description:
        "We develop and deploy in focused milestones with clear acceptance criteria and delivery checkpoints."
    },
    {
      step: "04",
      title: "Automate and optimize",
      description:
        "We automate repetitive workflows, monitor outcomes, and improve throughput using real data and user behavior."
    },
    {
      step: "05",
      title: "Scale with custom technology",
      description:
        "We expand system capability as your business grows, ensuring your stack remains stable, adaptive, and profitable."
    }
  ],
  caseStudies: []
};

const Button = ({ children, variant = "primary", size = "md", className = "", ...props }) => {
  const base =
    "inline-flex min-h-[48px] items-center justify-center rounded-xl font-bold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0";
  const variants = {
    primary: "bg-[var(--accent)] text-[var(--accent-ink)] hover:scale-[1.02] hover:brightness-95 active:scale-[0.99]",
    secondary: "border border-[var(--border-soft)] bg-transparent text-[var(--text-strong)] hover:bg-[var(--surface-soft)] hover:scale-[1.02] active:scale-[0.99]",
    ghost: "text-[var(--text-muted)] hover:bg-[var(--surface-soft)] hover:text-[var(--text-strong)]"
  };
  const sizes = {
    sm: "h-12 min-w-[120px] px-4 text-sm sm:h-10",
    md: "h-12 min-w-[148px] px-6 text-sm",
    lg: "h-12 min-w-[min(100%,180px)] px-6 text-base sm:min-w-[180px] sm:px-7"
  };
  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  );
};

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`fade-up-section py-14 sm:py-20 md:py-28 ${className}`}>
    <div className="mx-auto w-full max-w-[1200px] px-5 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const useInView = (rootMargin = "-80px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, inView];
};

const Counter = ({ target, suffix = "", duration = 900 }) => {
  const [ref, inView] = useInView("-40px");
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    let frame = null;
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [duration, inView, target]);

  return (
    <span ref={ref} className="font-black text-[var(--text-strong)]">
      {value}
      {suffix}
    </span>
  );
};

const BlockEndDivider = ({ scrollToId }) => {
  const [ref, inView] = useInView("-72px");
  const goNext = () => {
    if (scrollToId) {
      document.getElementById(scrollToId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollBy({ top: Math.min(window.innerHeight * 0.88, 720), behavior: "smooth" });
    }
  };

  return (
    <div ref={ref} className={`block-end-divider${inView ? " block-end-divider--visible" : ""}`}>
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-8">
        <div className="block-end-divider__track">
          <span className="block-end-divider__line" aria-hidden />
          <button
            type="button"
            className="block-end-divider__knob"
            onClick={goNext}
            aria-label={scrollToId ? `Continue to next section` : "Scroll down to next content"}
          >
            <ChevronRight className="h-5 w-5 text-[#ffffff]" strokeWidth={2.5} aria-hidden />
          </button>
          <span className="block-end-divider__line" aria-hidden />
        </div>
      </div>
    </div>
  );
};

const MobileTechStrip = () => (
  <div className="mt-8 flex flex-wrap items-center justify-center gap-2 md:hidden" aria-hidden>
    {[Code2, Settings2, Bot, Database, Cpu].map((Icon, i) => (
      <div key={i} className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-2">
        <Icon className="h-4 w-4 text-[var(--accent)]" />
      </div>
    ))}
  </div>
);

const MouseTechIcon = () => (
  <div className="pointer-events-none absolute inset-0 z-[2] hidden md:block" aria-hidden>
    <div className="floating-tech-icon floating-tech-icon-a">
      <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface)]/80 p-2 shadow-[0_0_24px_color-mix(in_srgb,var(--accent)_25%,transparent)] backdrop-blur-sm">
        <Code2 className="h-4 w-4 text-[var(--accent)]" />
      </div>
    </div>
    <div className="floating-tech-icon floating-tech-icon-b">
      <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface)]/70 p-2">
        <Settings2 className="h-4 w-4 text-[var(--accent)]" />
      </div>
    </div>
    <div className="floating-tech-icon floating-tech-icon-c">
      <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface)]/70 p-2">
        <Bot className="h-4 w-4 text-[var(--accent)]" />
      </div>
    </div>
    <div className="floating-tech-icon floating-tech-icon-d">
      <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface)]/70 p-2">
        <Database className="h-4 w-4 text-[var(--accent)]" />
      </div>
    </div>
    <div className="floating-tech-icon floating-tech-icon-e">
      <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface)]/70 p-2">
        <Cpu className="h-4 w-4 text-[var(--accent)]" />
      </div>
    </div>
  </div>
);

const Navbar = ({ theme, toggleTheme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const links = useMemo(
    () => [
      { to: "/", label: "Home" },
      { to: "/services", label: "Services" },
      { to: "/systems", label: "Systems" },
      { to: "/audit", label: "Audit" }
    ],
    []
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "border-b border-[var(--border-soft)] bg-[var(--bg)]/95 backdrop-blur-md" : "bg-transparent"}`}>
      <div className="mx-auto flex min-h-[3.5rem] w-full max-w-[1200px] items-center justify-between gap-2 px-4 py-2 sm:min-h-[4rem] sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 max-w-[calc(100%-3.25rem)] items-center gap-2.5 sm:max-w-none sm:gap-3">
          <span className="relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[var(--accent)]/45 bg-[var(--surface)] shadow-sm sm:h-11 sm:w-11">
            <img
              src={BRAND_LOGO}
              alt="MANSOK DIGITAL Solutions logo"
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.style.display = "none";
                const next = event.currentTarget.nextElementSibling;
                if (next) {
                  next.classList.remove("hidden");
                  next.classList.add("flex");
                }
              }}
            />
            <span className="absolute inset-0 hidden items-center justify-center text-[10px] font-black text-[var(--accent)]">
              MDS
            </span>
          </span>
          <span className="min-w-0 text-sm font-extrabold leading-tight tracking-tight text-[var(--text-strong)] sm:text-lg">
            <span className="block sm:inline">MANSOK </span>
            <span style={{ color: ACCENT }}>DIGITAL</span>
            <span className="block text-[var(--text-muted)] sm:inline sm:text-[var(--text-strong)]"> Solutions</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-7 md:flex">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition ${location.pathname === link.to ? "text-[var(--text-strong)]" : "text-[var(--text-muted)] hover:text-[var(--text-strong)]"}`}
            >
              {link.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border-soft)] text-[var(--text-strong)] hover:bg-[var(--surface-soft)]"
          >
            <SunMoon className="h-4 w-4" />
          </button>
          <Button size="sm" onClick={() => (window.location.href = "/audit")}>
            Free Audit
          </Button>
        </nav>
        <button
          type="button"
          aria-label="Open menu"
          className="inline-flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-xl text-[var(--text-strong)] md:hidden"
          onClick={() => setIsOpen((v) => !v)}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-[var(--border-soft)] bg-[var(--bg)] px-4 py-3 md:hidden">
          <div className="mx-auto flex max-w-[1200px] flex-col gap-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-xl px-3 py-3.5 text-base font-medium text-[var(--text-strong)] active:bg-[var(--surface-soft)]"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={toggleTheme}
              className="mt-1 inline-flex min-h-[48px] items-center justify-center rounded-xl border border-[var(--border-soft)] px-4 text-sm font-semibold text-[var(--text-strong)]"
            >
              Theme: {theme === "dark" ? "Dark" : "Light"}
            </button>
            <Button size="sm" onClick={() => (window.location.href = "/audit")}>
              Free Audit
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <>
  <Section id="home" className="relative overflow-hidden pb-16 pt-24 sm:pb-20 sm:pt-28 md:pb-24 md:pt-40">
    <div className="tech-grid-bg" />
    <MouseTechIcon />
    <div className="particle particle-a" />
    <div className="particle particle-b" />
    <div className="particle particle-c" />

    <div className="relative z-10 grid items-center gap-8 sm:gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
      <div>
        <p className="mb-4 inline-flex rounded-full border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--text-muted)] sm:mb-5 sm:text-xs sm:tracking-[0.18em]">
          High-Performance Tech Company
        </p>
        <h1 className="max-w-4xl text-3xl font-extrabold leading-[1.08] tracking-tight text-[var(--text-strong)] sm:text-4xl md:text-6xl">
          We Build Revenue-Driving Technology
          <br />
          for Businesses That Want to Win
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[var(--text-muted)] sm:mt-6 sm:text-base md:text-lg">
          MANSOK DIGITAL Solutions engineers websites, automation, and AI systems that eliminate inefficiency and increase profit - fast.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:gap-4">
          <Button className="w-full sm:w-auto" size="lg" onClick={() => (window.location.href = "/audit")}>
            Book Your Free Audit <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
          <Button className="w-full sm:w-auto" variant="secondary" size="lg" onClick={() => (window.location.href = "/systems")}>
            View Our Systems
          </Button>
        </div>
        <MobileTechStrip />
      </div>

      <div className="rounded-2xl border border-[var(--border-soft)] bg-gradient-to-b from-[var(--surface)] to-[var(--surface-2)] p-5 sm:p-6">
        <p className="text-xs font-semibold text-[var(--text-muted)] sm:text-sm">Performance snapshot</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:mt-6 sm:grid-cols-3 sm:gap-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] p-4 sm:block sm:gap-0">
            <p className="text-2xl sm:text-2xl">
              <Counter target={45} suffix="%" />
            </p>
            <p className="text-right text-xs text-[var(--text-muted)] sm:mt-1 sm:text-left">Higher conversions</p>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] p-4 sm:block sm:gap-0">
            <p className="text-2xl">
              <Counter target={3} suffix="x" />
            </p>
            <p className="text-right text-xs text-[var(--text-muted)] sm:mt-1 sm:text-left">Faster follow-up</p>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] p-4 sm:block sm:gap-0">
            <p className="text-2xl">
              <Counter target={98} suffix="%" />
            </p>
            <p className="text-right text-xs text-[var(--text-muted)] sm:mt-1 sm:text-left">Client retention</p>
          </div>
        </div>
      </div>
    </div>
  </Section>
  <BlockEndDivider scrollToId="problem" />
  </>
);

const ProblemSection = () => {
  const problems = [
    { icon: <PhoneMissed className="h-5 w-5 shrink-0 text-[var(--accent)]" />, label: "Missed calls" },
    { icon: <Timer className="h-5 w-5 shrink-0 text-[var(--accent)]" />, label: "Slow follow-ups" },
    { icon: <Link2Off className="h-5 w-5 shrink-0 text-[var(--accent)]" />, label: "Disconnected systems" },
    { icon: <AlertTriangle className="h-5 w-5 shrink-0 text-[var(--accent)]" />, label: "Websites that don't convert" }
  ];

  return (
    <>
    <Section id="problem" className="border-y border-[var(--border-soft)] bg-[var(--surface-2)]">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)]">The Problem</p>
          <h2 className="mt-4 text-2xl font-extrabold leading-tight text-[var(--text-strong)] sm:text-3xl md:text-5xl">Most businesses are leaking money through:</h2>
          <ul className="mt-8 space-y-4">
            {problems.map((item) => (
              <li key={item.label} className="flex items-center gap-3 text-base font-medium text-[var(--text-strong)]">
                {item.icon}
                {item.label}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xl font-bold text-[var(--text-strong)]">
            Technology isn&apos;t the problem.
            <br />
            <span style={{ color: ACCENT }}>Bad systems are.</span>
          </p>
        </div>

        <div className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-soft)] p-6">
          <p className="mb-4 text-sm font-semibold text-[var(--text-muted)]">System Leakage Map</p>
          <div className="grid gap-3">
            <div className="flex items-center justify-between rounded-xl border border-[var(--danger-border)] bg-[var(--danger-bg)] px-4 py-3 text-sm text-[var(--danger-text)]">
              <span>Lead Inflow</span>
              <span>-22%</span>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[var(--danger-border)] bg-[var(--danger-bg)] px-4 py-3 text-sm text-[var(--danger-text)]">
              <span>Follow-up Delay</span>
              <span>-31%</span>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[var(--danger-border)] bg-[var(--danger-bg)] px-4 py-3 text-sm text-[var(--danger-text)]">
              <span>Conversion Loss</span>
              <span>-18%</span>
            </div>
            <div className="mt-2 flex items-center justify-between rounded-xl border border-[var(--success-border)] bg-[var(--success-bg)] px-4 py-3 text-sm font-semibold text-[var(--success-text)]">
              <span>Recovered with optimized system</span>
              <span>+42%</span>
            </div>
          </div>
        </div>
      </div>
    </Section>
    <BlockEndDivider scrollToId="services" />
    </>
  );
};

const Services = () => {
  return (
    <>
    <Section id="services">
      <div className="text-center">
        <h2 className="text-2xl font-extrabold text-[var(--text-strong)] sm:text-3xl md:text-5xl">What We Engineer</h2>
      </div>
      <div className="mt-8 grid gap-5 sm:mt-12 sm:gap-6 md:grid-cols-3">
        {LONG_CONTENT.services.map((group) => (
          <article key={group.title} className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-5 transition-all duration-300 hover:border-[var(--accent)]/50 hover:shadow-[0_0_0_1px_var(--accent)] sm:p-6 md:hover:-translate-y-1">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--accent-soft)] text-[var(--accent)]">{group.icon}</div>
            <h3 className="text-xl font-bold text-[var(--text-strong)]">{group.title}</h3>
            <p className="mt-3 text-sm text-[var(--text-muted)]">{group.intro}</p>
            <ul className="mt-5 space-y-3">
              {group.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-soft)]">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--accent)]" />
                  {item}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
      <div className="mt-10 rounded-xl border border-[var(--success-border)] bg-[var(--success-bg)] px-6 py-4 text-center text-sm font-semibold text-[var(--success-text)] md:text-base">
        If it doesn&apos;t increase speed, clarity, or profit - we don&apos;t build it.
      </div>
    </Section>
    <BlockEndDivider scrollToId="process" />
    </>
  );
};

const ServicesDeepContent = () => (
  <>
  <Section id="delivery" className="pt-0">
    <div className="grid gap-6 md:grid-cols-2">
      <article className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-6">
        <h3 className="text-xl font-bold text-[var(--text-strong)]">Delivery Scope</h3>
        <p className="mt-3 text-sm text-[var(--text-muted)]">
          Every implementation includes strategy, architecture, build, deployment, and optimization. We prioritize execution speed without sacrificing system quality.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-[var(--text-soft)]">
          <li>- Discovery workshops and KPI alignment</li>
          <li>- Technical architecture and system mapping</li>
          <li>- UI implementation and content structure</li>
          <li>- CRM and automation integration</li>
          <li>- Analytics, reporting, and iteration loops</li>
        </ul>
      </article>
      <article className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-6">
        <h3 className="text-xl font-bold text-[var(--text-strong)]">Engagement Model</h3>
        <p className="mt-3 text-sm text-[var(--text-muted)]">
          We work in defined milestones with frequent releases and measurable outputs, so your team sees progress every week.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-[var(--text-soft)]">
          <li>- Weekly sprint delivery and progress reporting</li>
          <li>- Shared roadmap with transparent priorities</li>
          <li>- QA and reliability checks before release</li>
          <li>- Team handover and documentation support</li>
          <li>- Post-launch optimization and scaling plan</li>
        </ul>
      </article>
    </div>
  </Section>
  <BlockEndDivider scrollToId="process" />
  </>
);

const Process = () => {
  return (
    <>
    <Section id="process" className="border-y border-[var(--border-soft)] bg-[var(--surface-2)]">
      <div className="text-center">
        <h2 className="text-2xl font-extrabold text-[var(--text-strong)] sm:text-3xl md:text-5xl">How We Work</h2>
      </div>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {LONG_CONTENT.process.map((step, index) => (
          <div key={step.step} className="group relative overflow-hidden rounded-xl border border-[var(--border-soft)] bg-[var(--surface)] p-5">
            <p className="text-4xl font-extrabold text-[var(--text-muted)]/40 transition sm:text-5xl group-hover:text-[var(--accent)]/35">{step.step}</p>
            <p className="mt-3 text-sm font-semibold text-[var(--text-strong)]">{step.title}</p>
            <p className="mt-2 text-xs leading-relaxed text-[var(--text-muted)]">{step.description}</p>
            {index < 4 && <div className="absolute -right-2 top-1/2 hidden h-[1px] w-4 bg-[var(--accent)]/50 lg:block" />}
          </div>
        ))}
      </div>
    </Section>
    <BlockEndDivider scrollToId="positioning" />
    </>
  );
};

const Differentiation = () => (
  <>
  <Section id="positioning">
    <div className="mx-auto max-w-4xl text-center">
      <p className="text-2xl font-extrabold leading-tight text-[var(--text-strong)] sm:text-3xl md:text-5xl">
        <span className="text-[var(--text-muted)] line-through">We are not a marketing agency.</span>
        <br />
        <span style={{ color: ACCENT }}>We are a technology company.</span>
      </p>
    </div>
  </Section>
  <BlockEndDivider scrollToId="audit" />
  </>
);

const Systems = () => {
  return (
    <>
    <Section id="systems" className="border-y border-[var(--border-soft)] bg-[var(--surface-2)]">
      <div className="mb-8">
        <h2 className="text-2xl font-extrabold text-[var(--text-strong)] sm:text-3xl md:text-5xl">We Build Business Systems - Not Just Software</h2>
        <p className="mt-4 max-w-3xl text-[var(--text-muted)]">
          Software without systems creates chaos. We engineer connected systems that handle leads, customers, and operations automatically.
        </p>
      </div>
      <h3 className="mb-6 text-2xl font-bold text-[var(--text-strong)]">System Principles</h3>
      <div className="grid gap-6 md:grid-cols-2">
        {LONG_CONTENT.systemPrinciples.map((item) => (
          <article key={item.title} className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-6">
            <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--surface-soft)]">
              <Settings2 className="h-5 w-5 text-[var(--accent)]" />
            </div>
            <h4 className="text-lg font-bold text-[var(--text-strong)]">{item.title}</h4>
            <p className="mt-2 text-sm text-[var(--text-muted)]">{item.description}</p>
          </article>
        ))}
      </div>
      <div className="mt-10">
        <Button size="lg" onClick={() => (window.location.href = "/audit")}>
          Get Your System Audit <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </Section>
    <BlockEndDivider scrollToId="audit" />
    </>
  );
};

const AuditCTA = ({ onContact, blockEndScrollToId }) => (
  <>
  <Section id="audit">
    <div className="rounded-2xl border border-[var(--accent)]/35 bg-gradient-to-br from-[var(--surface)] to-[var(--surface-2)] p-6 text-center sm:p-8 md:p-12">
      <h2 className="text-2xl font-extrabold text-[var(--text-strong)] sm:text-3xl md:text-5xl">Start with a free system audit.</h2>
      <p className="mx-auto mt-4 max-w-2xl text-[var(--text-muted)]">Leave with clarity - whether you work with us or not.</p>
      <Button className="mt-8 shadow-[0_0_35px_color-mix(in_srgb,var(--accent)_35%,transparent)]" size="lg" onClick={onContact}>
        Book Your Free Audit <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  </Section>
  <BlockEndDivider scrollToId={blockEndScrollToId} />
  </>
);

const Contact = () => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("Sending...");
    try {
      const response = await fetch("https://formspree.io/f/xqegwlqg", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setStatus("Message sent successfully.");
        setFormData({ firstName: "", lastName: "", email: "", company: "", message: "" });
      } else {
        setStatus("Failed to send message. Please try again.");
      }
    } catch (error) {
      setStatus("Connection issue. Please try again.");
    }
  };

  return (
    <>
    <Section id="contact" className="pt-0">
      <div className="grid gap-8 rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-6 md:grid-cols-2 md:p-8">
        <div>
          <h3 className="text-2xl font-bold text-[var(--text-strong)] md:text-3xl">Let&apos;s build your growth system</h3>
          <p className="mt-3 text-sm text-[var(--text-muted)]">Tell us your current bottleneck. We will respond with a practical next step.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="h-12 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 text-sm text-[var(--text-strong)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--accent)]" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First name" />
            <input className="h-12 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 text-sm text-[var(--text-strong)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--accent)]" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" />
          </div>
          <input className="h-12 w-full rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 text-sm text-[var(--text-strong)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--accent)]" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email address" />
          <input className="h-12 w-full rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 text-sm text-[var(--text-strong)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--accent)]" name="company" value={formData.company} onChange={handleChange} placeholder="Company name" />
          <textarea className="min-h-[120px] w-full rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-3 text-sm text-[var(--text-strong)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--accent)]" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your bottleneck and goals..." />
          <Button className="h-12 w-full" type="submit">
            Send Message
          </Button>
          {status && <p className="text-xs text-[var(--text-muted)]">{status}</p>}
        </form>
      </div>
    </Section>
    <BlockEndDivider />
    </>
  );
};

const Footer = () => (
  <footer className="border-t border-[var(--border-soft)] bg-[var(--surface-2)] py-8">
    <div className="mx-auto flex w-full max-w-[1200px] flex-col items-stretch justify-between gap-6 px-5 sm:px-6 lg:flex-row lg:items-center lg:px-8">
      <div className="text-center sm:text-left">
        <p className="font-bold text-[var(--text-strong)]">MANSOK DIGITAL Solutions</p>
        <p className="text-xs text-[var(--text-muted)]">© 2026 MANSOK DIGITAL Solutions</p>
        <p className="mt-2 text-sm text-[var(--text-soft)]">+252614375195 · info@mansok.com</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-[var(--text-muted)] sm:justify-end sm:gap-6">
        <a href="#!" className="min-h-[44px] min-w-[44px] px-2 py-3 hover:text-[var(--text-strong)]">
          Privacy Policy
        </a>
        <a href="#!" className="min-h-[44px] min-w-[44px] px-2 py-3 hover:text-[var(--text-strong)]">
          Terms of Service
        </a>
      </div>
    </div>
  </footer>
);

const HomePage = () => (
  <>
    <Hero />
    <ProblemSection />
    <Services />
    <Process />
    <Differentiation />
    <AuditCTA onContact={() => (window.location.href = "/audit")} />
  </>
);

const ServicesPage = () => (
  <>
    <Section className="pt-24 pb-10 sm:pt-32 sm:pb-14 md:pt-40">
      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <p className="inline-flex rounded-full border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--text-muted)]">
            Service Architecture
          </p>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight text-[var(--text-strong)] sm:text-4xl md:text-6xl">Services Built for Speed and Outcomes</h1>
          <p className="mt-4 max-w-3xl text-[var(--text-muted)]">
            End-to-end technology delivery for businesses that need speed, reliability, and measurable revenue outcomes.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button className="w-full sm:w-auto" onClick={() => (window.location.href = "/audit")}>
              Start System Audit
            </Button>
            <Button className="w-full sm:w-auto" variant="secondary" onClick={() => (window.location.href = "/contact")}>
              Discuss Requirements
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface)] p-4">
            <p className="text-xs text-[var(--text-muted)]">Delivery model</p>
            <p className="mt-1 text-sm font-semibold text-[var(--text-strong)]">Sprint-based implementation</p>
          </div>
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface)] p-4">
            <p className="text-xs text-[var(--text-muted)]">Priority</p>
            <p className="mt-1 text-sm font-semibold text-[var(--text-strong)]">Revenue and response speed</p>
          </div>
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface)] p-4">
            <p className="text-xs text-[var(--text-muted)]">Output</p>
            <p className="mt-1 text-sm font-semibold text-[var(--text-strong)]">Systems your team can run daily</p>
          </div>
        </div>
      </div>
    </Section>
    <BlockEndDivider scrollToId="services" />
    <Services />
    <ServicesDeepContent />
    <Process />
    <AuditCTA onContact={() => (window.location.href = "/contact")} />
  </>
);

const SystemsPage = () => (
  <>
    <Section className="pt-24 pb-10 sm:pt-32 sm:pb-14 md:pt-40">
      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <p className="inline-flex rounded-full border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--text-muted)]">
            Systems Strategy
          </p>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight text-[var(--text-strong)] sm:text-4xl md:text-6xl">Connected Systems for Daily Execution</h1>
          <p className="mt-4 max-w-3xl text-[var(--text-muted)]">
            We design system layers that connect lead capture, follow-up, CRM movement, and reporting into one clear operational flow.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button className="w-full sm:w-auto" onClick={() => (window.location.href = "/audit")}>
              Get System Audit
            </Button>
            <Button className="w-full sm:w-auto" variant="secondary" onClick={() => (window.location.href = "/services")}>
              See Service Stack
            </Button>
          </div>
        </div>
        <div className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)]">System Map</p>
          <div className="mt-4 grid gap-3 text-sm">
            <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-2 text-[var(--text-strong)]">Lead Capture</div>
            <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-2 text-[var(--text-strong)]">Qualification + Routing</div>
            <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-2 text-[var(--text-strong)]">CRM + Automation</div>
            <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-2 text-[var(--text-strong)]">Reporting + Optimization</div>
          </div>
        </div>
      </div>
    </Section>
    <BlockEndDivider scrollToId="systems" />
    <Systems />
    <AuditCTA onContact={() => (window.location.href = "/contact")} />
  </>
);

const AuditPage = () => (
  <>
    <Section className="pt-24 pb-10 sm:pt-32 sm:pb-14 md:pt-40">
      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <p className="inline-flex rounded-full border border-[var(--border-soft)] bg-[var(--surface-soft)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--text-muted)]">
            Diagnostic Audit
          </p>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight text-[var(--text-strong)] sm:text-4xl md:text-6xl">Free System Audit</h1>
          <p className="mt-4 max-w-3xl text-[var(--text-muted)]">
            We review your current stack, identify bottlenecks, and provide a practical plan you can execute immediately.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button className="w-full sm:w-auto" onClick={() => (window.location.href = "/contact")}>
              Submit Project Details
            </Button>
            <Button className="w-full sm:w-auto" variant="secondary" onClick={() => (window.location.href = "/systems")}>
              View System Principles
            </Button>
          </div>
        </div>
        <div className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface)] p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)]">What You Get</p>
          <ul className="mt-4 space-y-2 text-sm text-[var(--text-soft)]">
            <li>- Bottleneck diagnosis report</li>
            <li>- Priority roadmap by business impact</li>
            <li>- Architecture recommendations</li>
            <li>- Action plan for next 30-90 days</li>
          </ul>
        </div>
      </div>
    </Section>
    <BlockEndDivider scrollToId="audit" />
    <AuditCTA onContact={() => (window.location.href = "/contact")} blockEndScrollToId="contact" />
    <Contact />
  </>
);

function App() {
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem("site-theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("site-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text-strong)]">
      <SeoHead pathname={location.pathname} />
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/systems" element={<SystemsPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route
            path="/contact"
            element={
              <>
                <Section className="pt-24 sm:pt-32 md:pt-40">
                  <h1 className="text-3xl font-extrabold leading-tight text-[var(--text-strong)] sm:text-4xl md:text-6xl">Contact</h1>
                  <p className="mt-4 max-w-3xl text-[var(--text-muted)]">
                    Share your business goals and we will send a direct plan with timeline, system priorities, and delivery approach.
                  </p>
                </Section>
                <BlockEndDivider scrollToId="contact" />
                <Contact />
              </>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <FloatingWhatsApp
        phoneNumber="+252614375195"
        accountName="MANSOK DIGITAL SOLUTIONS"
        avatar={BRAND_LOGO}
        allowEsc
        allowClickAway
        notification
        notificationSound
      />
    </div>
  );
}

export default App;